import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ClipboardList, FileSpreadsheet, Search, ChevronDown, ChevronUp } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import api from "@/lib/api";
import Logo from "@/components/Logo";

const ACTIONS = ["crea", "modifica", "elimina", "ritiro", "notifica", "revoca_consenso"];
const ENTITIES = ["concorso", "premio", "lotto", "sede", "montepremi_sede", "ritiro", "contatto", "campagna", "cliente", "impostazioni", "report"];
const actionStyle = {
  crea: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  modifica: "bg-sky-500/15 text-sky-400 border-sky-500/30",
  elimina: "bg-red-500/15 text-red-400 border-red-500/30",
  ritiro: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  notifica: "bg-violet-500/15 text-violet-400 border-violet-500/30",
  revoca_consenso: "bg-red-500/15 text-red-300 border-red-500/30",
};

export default function AuditLog() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [logs, setLogs] = useState(null);
  const [f, setF] = useState({ actor: "", action: "", entity_type: "", date_from: "", date_to: "" });
  const [openId, setOpenId] = useState(null);

  const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
  const load = useCallback(async () => {
    try {
      setLogs(await api.listAuditLogs(params));
    } catch {
      toast.error("Errore nel caricamento del log");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(params)]);
  useEffect(() => {
    load();
  }, [load]);

  const sel = "h-10 rounded-md border border-border bg-background px-3 text-sm";
  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-background/80 border-b border-border">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center gap-3">
          <Button data-testid="audit-back-button" variant="ghost" size="icon" onClick={() => navigate("/")}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <Logo className="h-10" />
          <div>
            <h1 className="font-display font-bold text-xl">Log Attività</h1>
            <p className="text-xs text-muted-foreground">Chi ha fatto cosa, con data e ora italiana</p>
          </div>
          <a className="ml-auto" href={api.exportAuditLogsUrl(params)} data-testid="audit-export-button">
            <Button variant="secondary" className="gap-2"><FileSpreadsheet className="h-4 w-4" /> Esporta Excel</Button>
          </a>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8 space-y-4" data-testid="audit-page">
        <Card className="p-4 bg-card border-border grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-3 text-muted-foreground" />
            <Input data-testid="audit-filter-actor" className="pl-9" placeholder="Utente (email)" value={f.actor} onChange={(e) => setF({ ...f, actor: e.target.value })} />
          </div>
          <select data-testid="audit-filter-action" className={sel} value={f.action} onChange={(e) => setF({ ...f, action: e.target.value })}>
            <option value="">Tutte le azioni</option>
            {ACTIONS.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
          <select data-testid="audit-filter-entity" className={sel} value={f.entity_type} onChange={(e) => setF({ ...f, entity_type: e.target.value })}>
            <option value="">Tutte le entità</option>
            {ENTITIES.map((a) => <option key={a} value={a}>{a.replace("_", " ")}</option>)}
          </select>
          <Input data-testid="audit-filter-from" type="date" value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value })} />
          <Input data-testid="audit-filter-to" type="date" value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value })} />
        </Card>

        {!logs && <p className="text-muted-foreground">Caricamento…</p>}
        {logs?.length === 0 && (
          <Card className="p-10 text-center border-dashed border-2 bg-card/50" data-testid="audit-empty">
            <ClipboardList className="h-10 w-10 mx-auto text-muted-foreground/40" />
            <p className="text-muted-foreground mt-3">Nessuna attività registrata con questi filtri.</p>
          </Card>
        )}
        {logs?.length > 0 && (
          <Card className="bg-card border-border overflow-hidden">
            <table className="w-full text-sm">
              <thead className="text-left text-xs text-muted-foreground bg-secondary/40">
                <tr>
                  <th className="px-4 py-2">Data e ora</th>
                  <th className="px-4 py-2">Utente</th>
                  <th className="px-4 py-2">Azione</th>
                  <th className="px-4 py-2">Entità</th>
                  <th className="px-4 py-2">Nome</th>
                  <th className="px-4 py-2 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {logs.map((l) => (
                  <LogRow key={l.id} l={l} open={openId === l.id} toggle={() => setOpenId(openId === l.id ? null : l.id)} />
                ))}
              </tbody>
            </table>
          </Card>
        )}
        <p className="text-xs text-muted-foreground">{logs?.length ?? 0} eventi (max 200 mostrati; l'export Excel include tutto)</p>
      </main>
    </div>
  );
}

function LogRow({ l, open, toggle }) {
  const hasDetails = l.details && Object.keys(l.details).length > 0;
  return (
    <>
      <tr data-testid={`audit-row-${l.id}`} className="border-t border-border hover:bg-secondary/20 cursor-pointer" onClick={hasDetails ? toggle : undefined}>
        <td className="px-4 py-2 font-mono-code text-xs whitespace-nowrap">{l.created_at_local}</td>
        <td className="px-4 py-2 truncate max-w-[220px]">{l.actor}</td>
        <td className="px-4 py-2"><Badge className={`border ${actionStyle[l.action] || ""}`}>{l.action}</Badge></td>
        <td className="px-4 py-2 text-muted-foreground">{(l.entity_type || "").replace("_", " ")}</td>
        <td className="px-4 py-2 truncate max-w-[260px]">{l.entity_name || <span className="text-muted-foreground">—</span>}</td>
        <td className="px-4 py-2 text-muted-foreground">{hasDetails && (open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />)}</td>
      </tr>
      {open && (
        <tr className="border-t border-border/50 bg-secondary/10">
          <td colSpan={6} className="px-4 py-3">
            <pre data-testid={`audit-details-${l.id}`} className="text-xs font-mono-code whitespace-pre-wrap text-muted-foreground">{JSON.stringify(l.details, null, 2)}</pre>
            {l.entity_id && <p className="text-[11px] text-muted-foreground mt-1">ID: {l.entity_id}</p>}
          </td>
        </tr>
      )}
    </>
  );
}
